import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Field, Form, Formik } from 'formik';
import { Img } from 'react-image';
import ReactSpinner from 'react-spinjs-fix';

import Button, { alignCenter, red } from 'ui_system/forms/buttons';
import { Label } from 'ui_system/forms/FormStyles';
import SimpleDate from 'ui_system/forms/SimpleDate';
import {
  Col,
  makeColGroup,
  makePaddedWrapper,
  makeSection,
  makeTwoWide,
} from 'ui_system/layout/grids';
import { colors } from 'ui_system/compass';
import { Theme } from 'ui_system/theme';
import LocationIcn from 'views/ui/icons/location-icon';
import ComputerIcn from 'views/ui/icons/computer';
import ContentLoader from 'ui_system/utilities/ContentLoader';

import { t } from 'services/translation';
import { DEFAULT_OPTS as SPINNER_OPTIONS } from 'views/anyguide/helpers/spinner';
import { Grid } from 'views/anyguide/messages/styles';
import defaultImage from 'views/anyguide/tours/item/images/default_image.png';

import TimeSlotsContainer from './TimeSlotsContainer';

export const LoadingOverlay = styled.div`
  background: #fff;
  opacity: 0.5;
  z-index: 2;
`;

const TourImage = styled(Img)`
  width: 50px;
  display: inline-block;
`;

const TimeSlotsSection = styled.div`
  margin-bottom: 20px;
  width: 120px;
`;

const StartDate = styled(Grid)`
  grid-gap: 18px;
`;

const Name = styled.p`
  font-weight: 400;
`;

const CancellationPolicy = styled.div`
  font-size: 14px;

  div {
    font-weight: 400;
    text-align: center;
  }

  p {
    margin-top: 5px;
    color: ${colors.text.secondary};
  }
`;

const TimezoneText = styled.p`
  color: ${colors.text.icon};
  font-size: 12px;
`;

const layout = Theme.layout0;
const Section = makeSection({ layout });
const ColGroup = makeColGroup({ layout, Col });
const TwoWide = makeTwoWide({ Col, ColGroup });

const PaddedWrapperBase = makePaddedWrapper({ layout });
const PaddedWrapper = styled(PaddedWrapperBase)`
  padding: 15px 30px 25px 30px;

  @media (max-width: 767px) {
    padding: 42px 16px;
  }
`;

const LocationInfo = styled.div`
  align-items: center;
  color: ${colors.text.secondary};
  display: flex;
  font-size: 12px;
  margin-top: 4px;
`;

const iconCss = css`
  fill: ${colors.text.icon};
  height: 18px;
  margin-right: 3px;
  width: 18px;
`;

const LocationIcon = styled(LocationIcn)`
  ${iconCss};
`;

const ComputerIcon = styled(ComputerIcn)`
  ${iconCss};
`;

const Schedule = ({
  dateLabelText,
  timeLabelText,
  selectedDate,
  startDateToggled,
  availableDates,
  excludedDates,
  setDate,
  timeSelectorToggled,
  logoUrl,
  name,
  address,
  submit,
  loading,
  timezone,
  isOnline,
  isVisible = true,
  brandColor,
}) => {
  return (
    <Formik
      enableReinitialize
      initialValues={{}}
      validationSchema={null}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={() => {}}
      render={() => (
        <Form>
          <PaddedWrapper>
            <Section>
              <TwoWide>
                <TourImage
                  loader={
                    <ContentLoader
                      height="30px"
                      width={30}
                      style={{ marginRight: '5px' }}
                    />
                  }
                  unloader={<TourImage src={defaultImage} />}
                  src={logoUrl}
                />
                <Name>{name}</Name>
                <LocationInfo>
                  {isOnline ? <ComputerIcon /> : <LocationIcon />}
                  {isOnline ? 'Online' : address}
                </LocationInfo>
              </TwoWide>
            </Section>
            {isOnline && (
              <Section style={{ paddingTop: '5px', paddingBottom: '0px' }}>
                <TimezoneText>
                  {timezone && `Times shown in ${timezone}`}
                </TimezoneText>
              </Section>
            )}
            {loading ? (
              <Section style={{ height: '100px', position: 'relative' }}>
                <LoadingOverlay>
                  <ReactSpinner config={{ ...SPINNER_OPTIONS, radius: 20 }} />
                </LoadingOverlay>
              </Section>
            ) : (
              <React.Fragment>
                <Section>
                  <Field name="index" type="hidden" />
                  <Label block>{dateLabelText}</Label>
                  <StartDate gridTemplateColumns="50% 50%">
                    <Field
                      field={{
                        name: 'SimpleDate',
                        value: selectedDate,
                      }}
                      onToggled={startDateToggled}
                      component={SimpleDate}
                      availableDates={availableDates}
                      excludedDates={excludedDates}
                      handleDateChange={(_field, date) => setDate(date)}
                      displayFormat="MMM D, YYYY"
                      placeholder="Select"
                      brandColor={brandColor}
                    />
                  </StartDate>
                </Section>
                <Section>
                  <Label block>{timeLabelText}</Label>
                  <TimeSlotsSection last>
                    <Field
                      parentOpen={isVisible}
                      uiMode="selector"
                      timeSelectorToggled={timeSelectorToggled}
                      component={TimeSlotsContainer}
                    />
                  </TimeSlotsSection>
                </Section>
                <Section>
                  <CancellationPolicy>
                    <div>{t('CANCELLATION_POLICY')}</div>
                    <p>
                      {t(
                        'IF_NO_PARTICIPANTS_ARE_BOOKED_24_HOURS_BEFORE_THE_SCHEDULED_DATE_THE_CLASS_WILL_AUTOMATICALLY_BE_CANCELLED_AND_YOU_WILL_BE_NOTIFIED_VIA_EMAIL'
                      )}
                    </p>
                  </CancellationPolicy>
                </Section>
                <Section>
                  <Button
                    type="submit"
                    onClick={submit}
                    modifiers={[red, alignCenter]}
                  >
                    {t('SCHEDULE_CLASS')}
                  </Button>
                </Section>
              </React.Fragment>
            )}
          </PaddedWrapper>
        </Form>
      )}
    />
  );
};

export default Schedule;
